import * as React from 'react';
import locales from '../locales';
import {ClientSummary, Contact, GridRequest, PageResponseEnvelope} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {OptionProps, SelectValue} from 'antd/lib/select';
import {addIfNotIn, findValueById} from '../utils/utils';
import {Modal, Select} from 'antd';
import {normalizeForSearch} from "../utils/string-utils";
import ClientContactForm from "./ClientContactForm";
import {showSaveSuccessNotification} from "../utils/dialogs";
import DropdownRender from "../common/DropdownRender";

interface ClientProps {
  client: ClientSummary | undefined;
}

export function clientIdChanged(prevProps: Readonly<ClientProps>, props: Readonly<ClientProps>): boolean {
  const prevClientId = prevProps.client && prevProps.client.id;
  const clientId = props.client && props.client.id;
  return (prevClientId !== clientId);
}

interface ClientContactSelectorProps {
  client: ClientSummary | undefined;
  contact: Contact | undefined;
  onContactChange: (contact: Contact | undefined) => void;
  disabled: boolean;
}

interface ClientContactSelectorState {
  options?: Contact[];
  editedContact?: Contact;
}

class ClientContactSelector extends React.Component<ClientContactSelectorProps, ClientContactSelectorState> {

  public state: ClientContactSelectorState = {};
  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidUpdate(prevProps: Readonly<ClientContactSelectorProps>, prevState: Readonly<ClientContactSelectorState>) {
    if (clientIdChanged(prevProps, this.props)) {
      this.onClientChanged();
    }
  }

  public render(): React.ReactNode {
    const {contact} = this.props;
    const {options, editedContact} = this.state;

    // Don't set initial values until the options are available
    const value = options && contact && contact.id ? contact.id : undefined;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Select
          style={{width: '100%'}}
          showSearch={true}
          allowClear={true}
          placeholder={locales.choose}
          className="mild-disabled"
          disabled={this.isFieldDisabled()}
          value={value}
          onChange={this.onValueChange}
          filterOption={this.filterOptions}
          dropdownRender={this.renderDropdownOptions}
        >
          {options && options.map(this.renderOptions)}
        </Select>

        <Modal
          title={locales.add_contact}
          visible={editedContact !== undefined}
          footer={null}
          onCancel={this.onContactEditFormCancel}
          destroyOnClose={true}
        >
          {editedContact && <ClientContactForm
              contact={editedContact}
              onFormSubmit={this.onContactEditFormSubmit}
              onFormCancel={this.onContactEditFormCancel}
          />}
        </Modal>
      </>
    );
  }

  private filterOptions = (input: string, option: React.ReactElement<OptionProps>) => {
    const inp = normalizeForSearch(input);
    const opt = normalizeForSearch(option.props.title || "");
    return opt.indexOf(inp) >= 0;
  };

  private renderOptions = (c: Contact): React.ReactNode => {
    return (
      <Select.Option
        value={c.id ? c.id : undefined}
        key={String(c.id)}
        title={c.name}
      >
        {c.title ? <>{c.name}<span style={{fontSize: 12, color: '#aaaaaa'}}> - {c.title}</span></> : c.name}
      </Select.Option>
    );
  };

  private renderDropdownOptions = (optionItems: React.ReactNode) => {
    return (
      <DropdownRender options={optionItems} addItem={this.addItem}/>
    )
  };

  private addItem = () => {
    this.setState({
      editedContact: {
        id: null,
        name: '',
        title: null,
        tel: null,
        email: null,
        idNumber: null,
        mothersName: null,
        deleted: false,
      },
    });
  };

  private onContactEditFormCancel = () => {
    this.setState({editedContact: undefined});
  };

  private onContactEditFormSubmit = (contact: Contact) => {
    const clientId = this.props.client!.id!;
    withLoadingOverlay(
      furoApi.clients.createContact(clientId, contact)
        .then(this.onSaveContactSuccess)
      , this.loadingHandler);

  };

  private onSaveContactSuccess = () => {
    this.setState({editedContact: undefined});
    this.onClientChanged();
    showSaveSuccessNotification();
  };

  private isFieldDisabled = () => {
    const {client, disabled} = this.props;
    const hasClient = client !== undefined;
    return disabled || !hasClient;
  };

  private onValueChange = (value: SelectValue) => {
    this.props.onContactChange(findValueById(this.state.options, Number(value)));
  };

  private loadClientContacts = (clientId: number) => {
    const request: GridRequest = {pageSize: 1000, sortColumnKey: 'name', sortOrder: 'ascend'};
    const promise = furoApi.clients.filterContacts(clientId, request)
      .then(this.onClientContactsLoaded);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onClientContactsLoaded = (response: PageResponseEnvelope<Contact>) => {
    const options = response.results.content;
    this.addCurrentContactToOptionsIfNotFound(options);
    this.setState({options});
  };

  // If current contact is deleted, the backend won't include it in the options response, the dropdown
  // won't show it properly (just the ID/value). So we have to include the current contact in the options
  private addCurrentContactToOptionsIfNotFound(options: Contact[]) {
    const {contact} = this.props;
    if (contact) {
      addIfNotIn(options, contact);
    }
  }

  private onClientChanged = () => {
    const client = this.props.client;
    if (client && client.id !== null) {
      this.loadClientContacts(client.id);
    } else {
      this.clearClientContacts();
    }
  };

  private clearClientContacts = () => {
    this.props.onContactChange(undefined);
    this.setState({
      options: undefined,
    });
  };

}

export default ClientContactSelector;



// WEBPACK FOOTER //
// ./src/client-details/ClientContactSelector.tsx